import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import StatusBadge from "@/components/StatusBadge";
import { toast } from "sonner";

function NewShipmentDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [buyers, setBuyers] = useState([]);
  const [f, setF] = useState({ buyer_id: "", reference: "", origin: "", destination: "", items: "", eta: "", notes: "" });
  useEffect(() => {
    if (open) api.get("/admin/users?role=buyer&status=approved").then((r) => setBuyers(r.data)).catch(()=>{});
  }, [open]);
  const submit = async () => {
    try { await api.post("/admin/shipments", f); toast.success("Shipment created"); setOpen(false); setF({ buyer_id: "", reference: "", origin: "", destination: "", items: "", eta: "", notes: "" }); onCreated(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="new-shipment-btn">+ New shipment</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-heading tracking-tight">New shipment</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="md:col-span-2">
            <Label>Buyer</Label>
            <select data-testid="shp-buyer" className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm" value={f.buyer_id} onChange={(e)=>setF({...f,buyer_id:e.target.value})}>
              <option value="">Select buyer…</option>
              {buyers.map((b) => <option key={b.id} value={b.id}>{b.company_name || b.name} ({b.email})</option>)}
            </select>
          </div>
          <div><Label>Reference #</Label><Input data-testid="shp-ref" value={f.reference} onChange={(e)=>setF({...f,reference:e.target.value})}/></div>
          <div><Label>ETA</Label><Input data-testid="shp-eta" type="date" value={f.eta} onChange={(e)=>setF({...f,eta:e.target.value})}/></div>
          <div><Label>Origin</Label><Input data-testid="shp-origin" value={f.origin} onChange={(e)=>setF({...f,origin:e.target.value})} placeholder="e.g. Bangkok, Shanghai, Ho Chi Minh City"/></div>
          <div><Label>Destination</Label><Input data-testid="shp-dest" value={f.destination} onChange={(e)=>setF({...f,destination:e.target.value})} placeholder="Helsinki"/></div>
          <div className="md:col-span-2"><Label>Items</Label><Textarea data-testid="shp-items" rows={2} value={f.items} onChange={(e)=>setF({...f,items:e.target.value})}/></div>
          <div className="md:col-span-2"><Label>Notes</Label><Textarea data-testid="shp-notes" rows={2} value={f.notes} onChange={(e)=>setF({...f,notes:e.target.value})}/></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
          <Button data-testid="shp-save" onClick={submit} disabled={!f.buyer_id || !f.reference}>Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function UpdateShipmentDialog({ shipment, onSaved }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ status: shipment.status, location: "", note: "", eta: "" });
  useEffect(() => { if (open) setF({ status: shipment.status, location: "", note: "", eta: "" }); }, [open, shipment]);
  const submit = async () => {
    try { await api.patch(`/admin/shipments/${shipment.id}`, f); toast.success("Shipment updated"); setOpen(false); onSaved(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline" data-testid={`update-${shipment.id}`}>Update</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle className="font-heading tracking-tight">Update shipment {shipment.reference}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Status</Label>
            <select data-testid={`us-status-${shipment.id}`} className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm" value={f.status} onChange={(e)=>setF({...f,status:e.target.value})}>
              <option value="preparing">Preparing</option>
              <option value="in_transit">In transit</option>
              <option value="customs">Customs</option>
              <option value="delivered">Delivered</option>
              <option value="delayed">Delayed</option>
            </select>
          </div>
          <div><Label>Location</Label><Input data-testid={`us-loc-${shipment.id}`} value={f.location} onChange={(e)=>setF({...f,location:e.target.value})}/></div>
          <div><Label>ETA (optional)</Label><Input data-testid={`us-eta-${shipment.id}`} type="date" value={f.eta} onChange={(e)=>setF({...f,eta:e.target.value})}/></div>
          <div><Label>Note</Label><Textarea data-testid={`us-note-${shipment.id}`} rows={2} value={f.note} onChange={(e)=>setF({...f,note:e.target.value})}/></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
          <Button data-testid={`us-save-${shipment.id}`} onClick={submit}>Save update</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminShipments() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/shipments").then((r) => setItems(r.data)).catch(()=>{});
  useEffect(() => { load(); }, []);
  return (
    <DashboardLayout title="Shipments" actions={<NewShipmentDialog onCreated={load} />}>
      <Card>
        <CardHeader><CardTitle className="font-heading tracking-tight">{items.length} shipment{items.length===1?"":"s"}</CardTitle></CardHeader>
        <CardContent>
          {items.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground text-sm" data-testid="admin-shipments-empty">No shipments yet.</div>
          ) : (
            <Table data-testid="admin-shipments-table">
              <TableHeader><TableRow><TableHead>Reference</TableHead><TableHead>Buyer</TableHead><TableHead>Route</TableHead><TableHead>Status</TableHead><TableHead>ETA</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
              <TableBody>
                {items.map((s) => (
                  <TableRow key={s.id} data-testid={`admin-shp-${s.id}`}>
                    <TableCell className="font-medium text-secondary">{s.reference}</TableCell>
                    <TableCell>{s.buyer?.company_name || s.buyer?.name || "—"}</TableCell>
                    <TableCell>{s.origin} → {s.destination}</TableCell>
                    <TableCell><StatusBadge status={s.status} /></TableCell>
                    <TableCell>{s.eta || "—"}</TableCell>
                    <TableCell className="text-right"><UpdateShipmentDialog shipment={s} onSaved={load} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </DashboardLayout>
  );
}
